import { Injectable } from '@angular/core';
import { SymbolType } from '../../../../../domain/symbol.domain';
import { TradingAccount } from '../../../../../domain/account.domain';
import { EquityExtendedTerms, EquityOrderAction, OrderDateType, OrderItem, PriceType, Strategy } from '../../../domain';
import {
  CalculatorFractionalTypes,
  EquityFormModel,
  OrderFormParameters,
  OrderStateParameters
} from '../../domain/order-entry.domain';
import { CreateOrderFormStocksOptionsBaseFormService } from './create-order-stocks-options-base-form.service';

@Injectable()
export class CreateStockOrderFormService extends CreateOrderFormStocksOptionsBaseFormService<EquityFormModel> {

  /**
   * Initialize object that represent state of UI across multiple steps
   */
  initializeViewModel(selectedAccount?: TradingAccount,
                      orderFormParameters?: OrderFormParameters,
                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                      _strategy: Strategy = Strategy.SPL,
                      index: number = 0): EquityFormModel {
    this.initFormModel(selectedAccount);
    if (orderFormParameters?.orderStateParameters?.orderKey === undefined) {
      this.initFromParameters(selectedAccount, orderFormParameters);
    } else {
      // its copy order, load object
      this.temp.isCopyOrder = true;
      const orderDetails = orderFormParameters.orderDetails;
      const orderItem = orderDetails.orderTerms.order[index];

      this.processOpenTriggeredOrder(orderItem);
      this.copyOrder(orderItem);
      this.setOrderSymbol({
        symbol: orderItem.security.symbol,
        market: orderItem.security.market
      });
      this.setOrderFormPriceType(orderItem.extendedTerms as EquityExtendedTerms);
      this.setParameters(orderFormParameters?.orderStateParameters);
    }
    // set the fractional type
    this.setFractionalTradeType(orderFormParameters, index);
    return Object.assign({}, this.temp); // TODO double check
  }

  copyOrder(orderItem: OrderItem): void {
    this.setCopyOrderAction(orderItem);
    this.temp.orderQuantity = orderItem.quantity;
    this.temp.orderAmount = orderItem.dollarAmt;
    this.setOrderFocusedField(orderItem);

    const extendedTerms = orderItem.extendedTerms as EquityExtendedTerms;
    this.setOrderFormPriceType(extendedTerms);
    this.setOrderFormGoodTil(extendedTerms);
    this.setOrderShareholderStatus(orderItem.clientInfo);
    this.setOrderAllOrNone(extendedTerms);
  }

  setCopyOrderAction(orderItem: OrderItem): void {
    const orderAction = orderItem.extendedTerms.orderAction as EquityOrderAction;
    if (orderAction !== undefined) {
      this.temp.orderAction = orderAction;
    }
  }

  setOrderSymbol(parameters: Pick<OrderStateParameters, 'symbol' | 'market'>): void {
    this.setSymbol({
      symbol: parameters.symbol,
      market: parameters.market,
      symbolType: SymbolType.STOCK
    });
  }

  // filterParameters(parameters: OrderStateParameters, account: TradingAccount): OrderStateParameters {
  //   const isShortMarginAccount = this.isShortMarginAccount(account);
  //   if (isShortMarginAccount && parameters.securityType === SymbolType.EQUITY) {
  //     if (parameters.action === 'buy') { // TODO: inconsistent use of enum
  //       return Object.assign({}, parameters, {
  //         action: 'buy_to_cover'
  //       });
  //     } else if (parameters.action === 'sell') {
  //       return Object.assign({}, parameters, {
  //         action: 'short_sell'
  //       });
  //     }
  //   }
  //   return parameters;
  // }

  setOrderFormAction(parameters: Pick<OrderStateParameters, 'action' | 'quantity'>): void {
    if (parameters.action !== undefined) {
      const orderActionMap = {
        buy: EquityOrderAction.BUY,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        buy_to_cover: EquityOrderAction.BUY_TO_COVER,
        sell: EquityOrderAction.SELL,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        short_sell: EquityOrderAction.SHORT_SELL
      };
      this.temp.orderAction = orderActionMap[parameters.action];
    }
  }

  protected setOrderFormPriceType(extendedTerms: EquityExtendedTerms): void {
    this.temp.orderPrice = extendedTerms.orderType;
    this.setPriceAmount(extendedTerms);
    if (extendedTerms.orderType !== PriceType.MARKET && extendedTerms.trailingStopTerms !== undefined) {
      this.temp.orderPriceAmount.deltaLimit = extendedTerms.trailingStopTerms.limitDelta;
      this.temp.orderPriceAmount.deltaTrigger = extendedTerms.trailingStopTerms.deltaAmount;
      this.temp.orderPriceAmount.deltaPercent = extendedTerms.trailingStopTerms.deltaPercent;
    }
  }

  protected setParameters(parameters: OrderStateParameters): void {
    // watch out: bool flags follow js boolean definition from url string
    // ie flag="false" or "0" will come here as true, since any non-empty string is true
    if (parameters?.simplifiedOrder !== undefined) {
      this.temp.simplifiedOrder = parameters.simplifiedOrder;
    }

    if (parameters?.reverse !== undefined) {
      this.temp.reverse = Boolean(parameters.reverse);
    }

    if (parameters?.quantity !== undefined) {
      this.temp.orderQuantityLimit = parameters.quantity;
    }
  }

  // private isShortMarginAccount(account: Pick<TradingAccount, 'type'>): boolean {
  //   return Account.isMarginShort(account);
  // }

  private initFormModel(selectedAccount?: TradingAccount): void {
    this.temp = {
      orderAction: this.isShortMarginAccount(selectedAccount) ? EquityOrderAction.SHORT_SELL : EquityOrderAction.BUY,
      orderPrice: PriceType.MARKET,
      shareholderStatus: undefined,
      orderGoodTil: OrderDateType.DAY,
      orderGoodTilDate: undefined,
      orderAllOrNone: false,
      orderQuantity: undefined,
      selectedSymbol: undefined,
      orderPriceAmount: {
        priceLimit: undefined,
        priceTrigger: undefined,
        deltaLimit: undefined,
        deltaTrigger: undefined,
        deltaPercent: undefined
      }
    };
  }

  private setOrderFocusedField(orderItem: OrderItem): void {
    if(orderItem.quantity !== undefined) {
      this.temp.focusedField = 'quantity';
    } else if(orderItem.dollarAmt !== undefined) {
      this.temp.focusedField = 'amount';
    }
  }

  private setFractionalTradeType(orderFormParameters?: OrderFormParameters, index: number = 0): void {
    const orderDetails = orderFormParameters?.orderDetails;
    const orderItem = orderDetails?.orderTerms?.order[index];

    if (this.temp.reverse) {
      this.temp.fractionalType = CalculatorFractionalTypes.FRACTIONAL;
      if (orderItem?.dollarAmt) {
        this.temp.orderQuantity = orderItem.details.totalFilledQuantity;
      }
    } else {
      this.temp.fractionalType = orderItem?.dollarAmt ? CalculatorFractionalTypes.NOTIONAL : CalculatorFractionalTypes.FRACTIONAL;
    }

    this.temp.fractionalEligibility = orderFormParameters?.fractionalEligibility;
  }
}

